import { apiFetch } from '../../../shared/services/apiClient';
import { Bolsa, LineaDeBolsa, Estado, CreateBolsaDTO, CreateLineaDTO, UpdateLineaDTO, UpdateBolsaDTO, DiscardBolsasResult } from '../types';

const API_BASE = `${process.env.REACT_APP_API_URL || 'http://localhost:4000/api'}/cotizaciones`;

export class BolsasService {
  static async getEstados(): Promise<Estado[]> {
    const res = await apiFetch(`${API_BASE}/estados`);
    if (!res.ok) throw new Error('Error fetching estados');
    const data = await res.json();
    return data.data || [];
  }

  static async crearBolsa(dto: CreateBolsaDTO): Promise<Bolsa> {
    const res = await apiFetch(`${API_BASE}/bolsas`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(dto),
    });
    if (!res.ok) {
      const error = await res.json();
      throw new Error(error.error?.message || 'Error crear bolsa');
    }
    const data = await res.json();
    return data.data;
  }

  // SPEC-205 RN-002/RN-003 — el filtro de estado lo pide el consumidor y lo aplica el servidor;
  // sin filtro la URL es la de siempre y GET /bolsas devuelve todos los estados.
  static async listarBolsas(filtro?: { estado?: string }): Promise<Bolsa[]> {
    const query = filtro?.estado ? `?${new URLSearchParams({ estado: filtro.estado })}` : '';
    const res = await apiFetch(`${API_BASE}/bolsas${query}`);
    if (!res.ok) throw new Error('Error fetching bolsas');
    const data = await res.json();
    return data.data || [];
  }

  // GET /bolsas está acotado al usuario de sesión (placeholder mientras no hay auth), por lo
  // que devuelve vacío para las bolsas existentes; /centro es el mismo origen que alimenta el
  // Centro de Cotizaciones y NO filtra por usuario, así que sirve para resolver el destino de
  // "crear bolsa". Los ítems traen `id` y `estado_codigo` (mismo DTO que /bolsas).
  static async listarBolsasCentro(): Promise<Bolsa[]> {
    const res = await apiFetch(`${API_BASE}/centro?limit=500`);
    if (!res.ok) throw new Error('Error fetching bolsas del centro');
    const data = await res.json();
    return data.data || [];
  }

  static async obtenerBolsa(bolsaId: string): Promise<Bolsa> {
    const res = await apiFetch(`${API_BASE}/bolsas/${bolsaId}`);
    if (!res.ok) throw new Error('Error fetching bolsa');
    const data = await res.json();
    return data.data;
  }

  static async actualizarBolsa(bolsaId: string, dto: UpdateBolsaDTO): Promise<Bolsa> {
    const res = await apiFetch(`${API_BASE}/bolsas/${bolsaId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(dto),
    });
    if (!res.ok) {
      const error = await res.json();
      throw new Error(error.error?.message || 'Error actualizar bolsa');
    }
    const data = await res.json();
    return data.data;
  }

  static async agregarProducto(bolsaId: string, dto: CreateLineaDTO): Promise<{ bolsa: Bolsa; linea: LineaDeBolsa }> {
    const res = await apiFetch(`${API_BASE}/bolsas/${bolsaId}/lineas`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(dto),
    });
    if (!res.ok) {
      const error = await res.json();
      throw new Error(error.error?.message || 'Error agregar producto');
    }
    const data = await res.json();
    return data.data;
  }

  static async cambiarCantidad(bolsaId: string, lineaId: string, dto: UpdateLineaDTO): Promise<Bolsa> {
    const res = await apiFetch(`${API_BASE}/bolsas/${bolsaId}/lineas/${lineaId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(dto),
    });
    if (!res.ok) {
      const error = await res.json();
      throw new Error(error.error?.message || 'Error cambiar cantidad');
    }
    const data = await res.json();
    return data.data;
  }

  static async eliminarProducto(bolsaId: string, lineaId: string): Promise<{ message: string }> {
    const res = await apiFetch(`${API_BASE}/bolsas/${bolsaId}/lineas/${lineaId}`, {
      method: 'DELETE',
    });
    if (!res.ok) {
      const error = await res.json();
      throw new Error(error.error?.message || 'Error eliminar producto');
    }
    const data = await res.json();
    return data.data;
  }

  static async eliminarBolsa(bolsaId: string): Promise<{ message: string }> {
    const res = await apiFetch(`${API_BASE}/bolsas/${bolsaId}`, {
      method: 'DELETE',
    });
    if (!res.ok) {
      const error = await res.json();
      throw new Error(error.error?.message || 'Error eliminar bolsa');
    }
    const data = await res.json();
    return data.data;
  }

  // SPEC-119 (RN-002, UC-006, UC-007) — soft-delete en lote; único caller autorizado es
  // BagDeleteConfirm (RN-007). Un 409 es un éxito PARCIAL: las bolsas en `discarded` sí
  // quedaron borradas — no se trata como error de red, se retorna igual que el 200.
  static async discardBolsas(ids: string[]): Promise<DiscardBolsasResult> {
    const res = await apiFetch(`${API_BASE}/bolsas/discard`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ids }),
    });
    const body = await res.json();
    if (res.status === 200) {
      return body.data as DiscardBolsasResult;
    }
    if (res.status === 409) {
      return body.error.details as DiscardBolsasResult;
    }
    throw new Error(body.error?.message || 'Error al borrar bolsas');
  }

  static async guardarBorrador(bolsaId: string): Promise<Bolsa> {
    const res = await apiFetch(`${API_BASE}/bolsas/${bolsaId}/guardar-borrador`, {
      method: 'POST',
    });
    if (!res.ok) {
      const error = await res.json();
      throw new Error(error.error?.message || 'Error guardar borrador');
    }
    const data = await res.json();
    return data.data;
  }

  static async continuarCotizacion(bolsaId: string): Promise<{ bolsa: Bolsa; productos: any[] }> {
    const res = await apiFetch(`${API_BASE}/bolsas/${bolsaId}/continuar-cotizacion`, {
      method: 'POST',
    });
    if (!res.ok) {
      const error = await res.json();
      throw new Error(error.error?.message || 'Error continuar cotización');
    }
    const data = await res.json();
    return data.data;
  }
}
